import { Box, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { CVSectionContainer } from '../../../../../components/common/CVSectionContainer';
import { useEditMode } from "../../../../../context/EditModeContext";
import { TechnologyExperience } from '../../../../../models/technologyExperience.model';
import { PersonTechnologyService } from '../../../../../services/personTechnologyService';
import { ProjectService } from '../../../../../services/projectService';
import { ProjectExperience, Skill } from '../../../../../types/cv';
import { PersonTechnology } from '../../../../../types/technology';
import { FavoriteTechnologies } from './FavoriteTechnologies';
import { SkillsTabs } from './SkillsTabs';

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">In this section, all competencies that you have tagged in your projects are gathered, categorized into six different competency categories:</Text>
    <Text size="sm" ml={10}>• Leadership and management competencies</Text>
    <Text size="sm" ml={10}>• Solution competencies</Text>
    <Text size="sm" ml={10}>• Programming competencies</Text>
    <Text size="sm" ml={10}>• Framework and methodology competencies</Text>
    <Text size="sm" ml={10}>• Consulting competencies</Text>
    <Text size="sm" ml={10}>• Technology competencies</Text>
    <Text size="sm" mt={5}>If the competency you have entered is not in the system, it will be placed under 'Skills without a category'. You can then suggest which category the competence should belong to by manually dragging it to one of the categories.</Text>
  </Stack>
);

interface SkillsOverviewProps {
  projectExperience: ProjectExperience[];
  skills: Skill[];
  onChange: (skills: Skill[]) => void;
  personId?: number;
  defaultView?: 'cloud' | 'categories';
  refreshTrigger?: number;
}

// Helper function to calculate years of experience from project dates
const calculateYearsOfExperience = (fromDate: string): number => {
  if (!fromDate) return 1;

  try {
    // Handle ISO date strings
    const startDate = new Date(fromDate);
    const currentDate = new Date();
    const diffInMs = currentDate.getTime() - startDate.getTime();
    const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25);
    return Math.max(1, Math.round(diffInYears));
  } catch (error) {
    console.error('Error calculating years of experience:', error);
    return 1;
  }
};

export function SkillsOverview({ projectExperience, skills, onChange, personId, defaultView = 'cloud', refreshTrigger = 0 }: SkillsOverviewProps) {
  const { isEditMode } = useEditMode();
  const [localSkills, setLocalSkills] = useState<Skill[]>(skills || []);
  const previousSkillsRef = useRef<Skill[]>(skills || []);

  // New state for Skills & Technologies section
  const [allTechnologies, setAllTechnologies] = useState<PersonTechnology[]>([]);
  const [favoriteTechnologies, setFavoriteTechnologies] = useState<PersonTechnology[]>([]);
  const [technologiesLoading, setTechnologiesLoading] = useState<boolean>(true);
  const [favoritesLoading, setFavoritesLoading] = useState<boolean>(true);
  const [technologyExperience, setTechnologyExperience] = useState<TechnologyExperience[]>([]);

  console.log('SkillsOverview render - skills from props:', skills?.length, 'localSkills:', localSkills?.length);

  // Fetch all technologies and favorites
  useEffect(() => {
    if (!personId) return;

    const fetchTechnologies = async () => {
      try {
        setTechnologiesLoading(true);
        const personTechnologyService = new PersonTechnologyService();
        const projectService = new ProjectService();

        const [skillsData, techExperience] = await Promise.all([
          personTechnologyService.getPersonSkills(personId),
          projectService.getTechnologyExperienceByPerson(personId)
        ]);

        setTechnologyExperience(techExperience);

        // Convert SkillEntry to PersonTechnology format
        const convertedData: PersonTechnology[] = skillsData.map(skill => ({
          id: skill.id,
          technology: {
            id: skill.technology.id,
            name: skill.technology.name,
            categories: (skill.technology as any).categories || []
          },
          personId: skill.personId,
          favourite: skill.favourite,
          skillLevel: skill.skillLevel,
          yearsOfExperience: skill.yearsOfExperience,
          projects: skill.projects || [],
          workExperiences: skill.workExperiences || []
        }));

        setAllTechnologies(convertedData);

        // Group technologies by category
        const grouped: Record<string, PersonTechnology[]> = {};
        convertedData.forEach(tech => {
          if (tech.technology.categories && tech.technology.categories.length > 0) {
            tech.technology.categories.forEach(category => {
              if (!grouped[category.name]) {
                grouped[category.name] = [];
              }
              grouped[category.name].push(tech);
            });
          } else {
            if (!grouped['Uncategorized']) {
              grouped['Uncategorized'] = [];
            }
            grouped['Uncategorized'].push(tech);
          }
        });
      } catch (error) {
        console.error('Failed to fetch technologies:', error);
      } finally {
        setTechnologiesLoading(false);
      }
    };

    const fetchFavorites = async () => {
      try {
        setFavoritesLoading(true);
        const personTechnologyService = new PersonTechnologyService();
        const modelFavorites = await personTechnologyService.getPersonFavoriteTechnologies(personId);

        // Convert from model PersonTechnology to types PersonTechnology
        const typeFavorites: PersonTechnology[] = modelFavorites.map(fav => ({
          id: fav.id,
          technology: {
            id: fav.technology.id,
            name: fav.technology.name,
            categories: (fav.technology as any).categories || []
          },
          personId: fav.personId,
          favourite: fav.favourite,
          skillLevel: null,
          yearsOfExperience: null,
          projects: [],
          workExperiences: []
        }));

        setFavoriteTechnologies(typeFavorites);
      } catch (error) {
        console.error('Failed to fetch favorite technologies:', error);
      } finally {
        setFavoritesLoading(false);
      }
    };

    fetchTechnologies();
    fetchFavorites();
  }, [personId, refreshTrigger]);

  // Update/sync when props skills change
  useEffect(() => {
    console.log('Skills props changed:', skills?.length);
    if (skills && JSON.stringify(skills) !== JSON.stringify(previousSkillsRef.current)) {
      console.log('Updating local skills from props:', skills);
      setLocalSkills(skills);
      previousSkillsRef.current = skills;
    }
  }, [skills]);

  // Log for debugging
  useEffect(() => {
    if (skills?.length > 0) {
      console.log('Skills from props (sample):', skills.slice(0, 3));
    }
    if (localSkills?.length > 0) {
      console.log('Local skills (sample):', localSkills.slice(0, 3));
    } else {
      console.log('No local skills available');
    }
  }, [skills, localSkills]);

  // Save skills to localStorage whenever they change
  useEffect(() => {
    if (localSkills && localSkills.length > 0) {
      try {
        localStorage.setItem('cached_skills', JSON.stringify(localSkills));
        console.log('Saved skills to localStorage:', localSkills.length);
      } catch (e) {
        console.error('Error saving skills to localStorage:', e);
      }
    }
  }, [localSkills]);

  // Load cached skills on initial mount if no skills provided
  useEffect(() => {
    if ((!skills || skills.length === 0) && (!localSkills || localSkills.length === 0)) {
      try {
        const cachedSkills = localStorage.getItem('cached_skills');
        if (cachedSkills) {
          const parsedSkills = JSON.parse(cachedSkills) as Skill[];
          console.log('Loaded cached skills from localStorage:', parsedSkills.length);
          setLocalSkills(parsedSkills);
          onChange(parsedSkills); // Update parent component
        }
      } catch (e) {
        console.error('Error loading cached skills:', e);
      }
    }
  }, []);

  // Auto-add skills from project experience when in edit mode
  useEffect(() => {
    if (!projectExperience.length || !isEditMode) return;

    // Extract all unique skills from project technologies
    const allSkills = new Set<string>();
    projectExperience.forEach(project => {
      if (project.technologies && Array.isArray(project.technologies)) {
        project.technologies.forEach(personTech => {
          if (personTech?.technology?.name) {
            allSkills.add(personTech.technology.name);
          }
        });
      }
    });

    // Filter out skills that are already in localSkills
    const existingSkillNames = new Set(localSkills.map(skill => skill.name));
    const newSkills = Array.from(allSkills).filter(skillName => !existingSkillNames.has(skillName));

    if (!newSkills.length) return;

    // Create new skill objects for each new skill
    const skillsToAdd: Skill[] = newSkills.map(skillName => {
      // Calculate years of experience based on projects with this skill
      const relevantProjects = projectExperience.filter(proj =>
        proj.technologies?.some(tech => tech?.technology?.name === skillName)
      );

      let yearsExp = 1; // Default to 1 year
      if (relevantProjects.length > 0) {
        const earliestProject = relevantProjects
          .sort((a, b) => {
            const dateA = new Date(a.startDate);
            const dateB = new Date(b.startDate);
            return dateA.getTime() - dateB.getTime();
          })[0];

        yearsExp = calculateYearsOfExperience(earliestProject.startDate);
      }

      // All new skills go to Uncategorized by default
      return {
        id: `${Date.now()}-${skillName.replace(/\s+/g, '-').toLowerCase()}`,
        name: skillName,
        yearsOfExperience: yearsExp,
        category: 'Uncategorized',
        includeInCV: true
      };
    });

    // Add new skills to local skills
    const updatedSkills = [...localSkills, ...skillsToAdd];
    setLocalSkills(updatedSkills);
    onChange(updatedSkills);
  }, [projectExperience, localSkills, isEditMode, onChange]);


  return (
    <CVSectionContainer
      sectionId="skills-overview"
      title="Skills Overview"
      instructions={INSTRUCTIONS}
    >
      <Stack gap="lg">
        {/* Skills & Technologies Section */}
        {personId && (
          <Box>
            <FavoriteTechnologies
              technologies={favoriteTechnologies}
              loading={favoritesLoading}
            />

            <SkillsTabs
              technologies={allTechnologies}
              technologyExperience={technologyExperience}
              loading={technologiesLoading}
              defaultView={defaultView}
            />
          </Box>
        )}

        {/* Show message if no skills and not in edit mode */}
        {!isEditMode && (!skills || skills.length === 0) && !personId && (
          <Text color="dimmed">No skills available. Add skills through your project experiences.</Text>
        )}
      </Stack>
    </CVSectionContainer>
  );
} 
